// src/components/ListingCard/ListingCard.js
import React, { useEffect, useRef } from 'react';
import { string, func, bool, oneOfType } from 'prop-types';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import { Avatar, NamedLink, ResponsiveImage } from '../../components';

import css from './ListingCard.module.css';
import { getServices } from '../../config/requests';

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
        </div>
      ) : null}
    </div>
  );
};

const normalize = s =>
  (s || '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

const pickFirst = (...vals) =>
  vals.find(v => (typeof v === 'string' ? v.trim() : v)) || '';

export const ListingCardComponent = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo,
    sendQueryParams,
    isSuggestion,
    request
  } = props;

  const SERVICES = getServices(intl) || [];

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData = {}, state } = currentListing.attributes;
  const listingType = publicData?.listingType;
  const isDraft = state === 'draft';

  const serviceNorm = normalize(publicData?.service);
  const categoryNorm = normalize(publicData?.category);
  const isRecruitment =
    serviceNorm.includes('recruit') ||
    serviceNorm.includes('reclut') ||
    categoryNorm.includes('recruit') ||
    categoryNorm.includes('reclut') ||
    categoryNorm.includes('vacante');

  const classes = classNames(
    rootClassName || css.root,
    className,
    {
      [css.suggestionCard]: isSuggestion || listingType === 'request',
      [css.recruitmentCard]: isRecruitment,
    }
  );

  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;

  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  let { listingFields = [] } = config?.listing || {};
  let cloudFieldOptions = (listingFields.find(f => f.key == 'cloudExperience')?.enumOptions || [])
    .filter(opt => {
      return (publicData?.cloudExperience || []).includes(opt.option);
    })
    .map(opt => opt.label);

  function toQueryString(obj, prefix = '') {
    return Object.keys(obj).map(key => {
      const value = obj[key];
      const queryKey = prefix ? `${prefix}[${key}]` : key;

      if (typeof value === 'object' && !Array.isArray(value)) {
        return toQueryString(value, queryKey);
      } else if (Array.isArray(value)) {
        return value.map((item, index) => toQueryString({ [index]: item }, queryKey)).join('&');
      } else {
        return `${encodeURIComponent(queryKey)}=${encodeURIComponent(value)}`;
      }
    }).join('&');
  }

  const authorAnonimity = author?.attributes?.profile?.publicData?.anonimizeData ?? true;

  // ===== Título calculado =====
  const recruitmentVacancyName = pickFirst(
    publicData?.vacancyTitle,
    publicData?.vacancyName,
    publicData?.jobTitle,
    publicData?.positionTitle,
    publicData?.positionName,
    publicData?.roleTitle,
    publicData?.roleName,
    publicData?.jobName,
    publicData?.puesto,
    publicData?.nombreVacante,
    publicData?.name
  );
  const roleFieldConfig = listingFields.find(f => f.key === 'role');
  const roleOptions = roleFieldConfig?.enumOptions || [];
  const roleOptionValue = publicData?.role || null;
  const roleLabel = roleOptions.find(o => o.option === roleOptionValue)?.label || null;

  const rawTitle = (title || '').trim();
  const backupFromPublicData = pickFirst(
    publicData?.title,
    publicData?.nombrePerfil,
    publicData?.name,
    publicData?.service,
    roleLabel
  );

  const computedCardTitle = isRecruitment
    ? (recruitmentVacancyName || rawTitle || backupFromPublicData)
    : (rawTitle || backupFromPublicData);

  const cardTitle =
    computedCardTitle && computedCardTitle.trim().length > 0
      ? computedCardTitle
      : intl.formatMessage({ id: 'ListingCard.draft' });

  const slug = createSlug(cardTitle || 'listing');

  // === Detección de ROL para aplicar tema en /s ===
  const roleRaw =
    currentListing?.attributes?.publicData?.role ||
    currentListing?.author?.attributes?.profile?.publicData?.role ||
    currentListing?.author?.attributes?.profile?.publicData?.userType ||
    '';
  const role = String(roleRaw).toLowerCase().includes('consult')
    ? 'role-consultant'
    : 'role-company';
  const ACCENT = role === 'role-company' ? '#5e17eb' : '#c6ff7d';

  // ==== DOM hook a nivel card (para ocultar tarifa en recruitment) ====
  const cardRef = useRef(null);
  useEffect(() => {
    if (!isRecruitment || !cardRef.current) return;
    const root = cardRef.current;

    const hideTarifaRows = () => {
      const nodes = root.querySelectorAll('li, div, p, span, dt, dd');
      nodes.forEach(node => {
        const txt = normalize(node.textContent).replace(/\s+/g, ' ').trim();
        if (!txt) return;
        if (txt.includes('tarifa')) {
          const row =
            node.closest('li') ||
            node.closest('[class*="row"]') ||
            node.closest('[class*="detail"]') ||
            node;
          row.style.display = 'none';
        }
      });
    };

    hideTarifaRows();
    const mo = new MutationObserver(() => hideTarifaRows());
    mo.observe(root, { childList: true, subtree: true, characterData: true });
    return () => mo.disconnect();
  }, [isRecruitment, id]);

  return (
    <div
      ref={cardRef}
      data-ftw-recruitment={isRecruitment ? '1' : '0'}
      data-rolecard={role}
      style={{ '--ml-accent': ACCENT, '--marketplaceColor': ACCENT }}
    >
      <NamedLink
        className={classes}
        name="ListingPage"
        params={{ id, slug }}
        to={{ search: request ? '?' + toQueryString(request) : '' }}
        {...setActivePropsMaybe}
      >
        <Avatar
          avatarClass={css.avatar}
          className={css.avatarContainer}
          user={author}
          isAnonimized={authorAnonimity}
          image={SERVICES.find(service => service.option === publicData?.service)?.image || null}
          disableProfileLink
        />

        <div className={css.info}>
          {/* Precio */}
          <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} />

          <div className={css.mainInfo}>
            <div className={css.title}>
              {cardTitle}
              {isSuggestion ? <span className={css.titleSuffix}>|</span> : null}
              {isSuggestion && cloudFieldOptions.length ? (
                <div className={css.services}>{cloudFieldOptions.join(', ')}</div>
              ) : null}
            </div>

            {(showAuthorInfo || !authorAnonimity) ? (
              <div className={css.authorInfo}>
                <FormattedMessage id="ListingCard.author" values={{ authorName }} />
              </div>
            ) : null}

            {isSuggestion ? (
              <div className={css.listingDescription}>
                {currentListing?.attributes?.description || ''}
              </div>
            ) : null}
          </div>
        </div>
      </NamedLink>
    </div>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
  isSuggestion: false,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]).isRequired,
  showAuthorInfo: bool,
  isSuggestion: bool,
  renderSizes: string,
  setActiveListing: func,
};

export default injectIntl(ListingCardComponent);
